import type { Contact, SearchResult, SearchEntity } from '../types';

// ===== Ray Radcliff — verbatim from the live API sample provided =====
const ray: Contact = {
  id: 'mBYbgpP1ZQCWJTWBkKb8',
  ownerOrgId: 'EW5sJEouKhIcg3MUs4KC',
  saved: true,
  tags: [],
  statuses: [{ statusId: '0', projectId: 'R9ti7gHSenjaqgkPYDPC', active: false }],
  ai_skills: [
    'sales management', 'territory management', 'business administration',
    'paper sales', 'specification sales', 'distribution', 'client relations',
  ],
  profileData: {
    first_name: 'ray', last_name: 'radcliff', full_name: 'ray radcliff',
    location_name: 'grand prairie, texas, united states',
    summary: '',
    linkedin_url: 'linkedin.com/in/ray-radcliff-67b16a13',
    github_url: '',
    job_company_name: 'copamex', job_company_id: 'N1C9SRtKa6eNU7JPAZM2aw7fpt9o',
    job_company_linkedin_url: '',
    job_title: 'sales manager',
    current_job_location: '',
    skills: [
      'account management', 'b2b', 'budgets', 'business planning', 'business strategy',
      'change management', 'continuous improvement', 'cross functional team leadership',
      'customer service', 'english', 'erp', 'export', 'forecasting', 'international business',
      'international sales', 'key account management', 'logistics', 'management', 'manufacturing',
      'marketing strategy', 'microsoft excel', 'microsoft office', 'national accounts',
      'negotiation', 'new business development', 'offset printing', 'operations management',
      'packaging', 'paper industry', 'pricing', 'process improvement', 'procurement',
      'product development', 'purchasing', 'sales', 'sales management', 'sales operations',
      'sales presentations', 'sap', 'selling', 'strategic planning', 'supply chain',
    ],
    contact_info_availability: { work_email: true, personal_emails: false, phone_numbers: true },
    education: [
      {
        school: { id: 'rS2KTY6GVKNPiYmAJUrzPw_0', name: 'texas wesleyan university', website: 'txwes.edu', linkedin_url: 'linkedin.com/school/texas-wesleyan-university', type: 'post-secondary institution' },
        start_date: '1979-01', end_date: '1981-01',
        degrees: ['bachelor of business administration', 'bachelors', 'bba'],
        majors: ['business', 'business administration', 'business administration and management', 'management'],
      },
      {
        school: { name: 'jacksonville baptist college', type: 'post-secondary institution' },
        start_date: '1977-01', end_date: '1979',
        degrees: ['associate of arts', 'associates'],
        majors: ['business administration'],
      },
    ],
    experience: [
      {
        is_primary: true,
        company: { id: 'N1C9SRtKa6eNU7JPAZM2aw7fpt9o', name: 'copamex', website: 'copamex.com', linkedin_url: 'linkedin.com/company/copamex', industry: 'paper & forest products' },
        start_date: '2002-12', end_date: '',
        title: { name: 'sales manager' },
      },
      {
        company: { name: 'mead coated papers' },
        start_date: '1998-02', end_date: '2001-04',
        title: { name: 'specification coated paper sales' },
      },
      {
        company: { name: 'international paper fine paper division beckett papers' },
        start_date: '1997-01', end_date: '1999-09',
        title: { name: 'territory manager' },
        summary: 'Text and Cover sales through paper merchant distribution for a 5 state area.',
      },
    ],
  },
};

// ===== Other candidates — match the screenshot row-by-row =====
const anthony: Contact = {
  id: 'cAnthonyHan',
  saved: false, tags: [], ai_skills: [],
  statuses: [{ statusId: '0', projectId: 'R9ti7gHSenjaqgkPYDPC', active: false }],
  profileData: {
    first_name: 'anthony', last_name: 'han', full_name: 'anthony han',
    location_name: 'addison, texas, united states',
    linkedin_url: 'linkedin.com/in/anthony-han',
    job_company_name: 'emerson', job_title: 'regional sales manager',
    skills: ['erp', 'sales management', 'territory development', 'b2b'],
    education: [{
      school: { name: 'princeton university' },
      degrees: ['bachelors'], majors: ['business administration'],
      start_date: '1995-09', end_date: '1999-06',
    }],
    experience: [{
      is_primary: true,
      company: { name: 'emerson', industry: 'electrical/electronic manufacturing' },
      start_date: '2014-03', end_date: '',
      title: { name: 'regional sales manager' },
    }],
  },
};

const dolly: Contact = {
  id: 'cDollyChack', saved: false, tags: [], ai_skills: [],
  profileData: {
    first_name: 'dolly', last_name: 'chack', full_name: 'dolly chack',
    location_name: 'irving, texas, united states',
    linkedin_url: 'linkedin.com/in/dolly-chack',
    job_company_name: 'confidential', job_title: 'senior business development manager',
    skills: ['erp', 'business development', 'enterprise software'],
    experience: [{
      is_primary: true, company: { name: 'confidential' },
      start_date: '2019-04', end_date: '',
      title: { name: 'senior business development manager' },
    }],
  },
};

const pradeep: Contact = {
  id: 'cPradeepAlluri', saved: false, tags: [], ai_skills: [],
  profileData: {
    first_name: 'pradeep', last_name: 'alluri', full_name: 'pradeep alluri',
    location_name: 'irving, texas, united states',
    linkedin_url: 'linkedin.com/in/pradeep-alluri',
    job_company_name: 'workday', job_title: 'business development manager',
    skills: ['sap', 'erp', 'oracle erp', 'consulting', 'implementation'],
    education: [{
      school: { name: 'naveen jindal school of management, ut dallas' },
      degrees: ['master of business administration', 'mba'], majors: ['business administration'],
    }],
    experience: [{
      is_primary: true, company: { name: 'workday', industry: 'software' },
      start_date: '2020-08', end_date: '',
      title: { name: 'business development manager' },
    }],
  },
};

// generate filler candidates so pagination feels real
function makeFiller(i: number): Contact {
  const firstNames = ['Sarah', 'Michael', 'Priya', 'Carlos', 'Jenna', 'Ahmed', 'Lin', 'David', 'Megan', 'Tomás', 'Aisha'];
  const lastNames = ['Park', 'Chen', 'Patel', 'Garcia', 'Wright', 'Khan', 'Wu', 'Brown', 'O\'Neill', 'Mendez', 'Iqbal'];
  const titles = ['Sales Manager', 'Senior Account Executive', 'Regional Sales Director', 'Enterprise Sales Lead', 'VP of Sales', 'Sales Operations Manager'];
  const companies = ['Salesforce', 'Oracle', 'NetSuite', 'SAP', 'Microsoft', 'IBM', 'Workday', 'Infor'];
  const cities = ['Dallas', 'Plano', 'Irving', 'Frisco', 'Fort Worth', 'Arlington', 'Richardson'];
  const fn = firstNames[i % firstNames.length];
  const ln = lastNames[(i * 3) % lastNames.length];
  const company = companies[i % companies.length];
  const city = cities[i % cities.length];
  return {
    id: `c-filler-${i}`,
    saved: false, tags: [],
    profileData: {
      first_name: fn.toLowerCase(), last_name: ln.toLowerCase(),
      full_name: `${fn} ${ln}`,
      location_name: `${city}, texas, united states`,
      linkedin_url: `linkedin.com/in/${fn.toLowerCase()}-${ln.toLowerCase()}-${i}`,
      job_company_name: company, job_title: titles[i % titles.length],
      skills: ['erp', 'sales', 'b2b', 'crm'],
      experience: [{
        is_primary: true, company: { name: company },
        start_date: `${2015 + (i % 8)}-0${1 + (i % 9)}`, end_date: '',
        title: { name: titles[i % titles.length] },
      }],
    },
  };
}

const fillers = Array.from({ length: 11 }, (_, i) => makeFiller(i));

// ===== Search results =====
export const SAMPLE_RESULTS: SearchResult[] = [
  { contact: ray, reasons: [{ criterionIndex: 1, label: 'ERP', verdict: 'good', rationale: 'Skills list explicitly includes ERP experience.' }] },
  { contact: anthony, reasons: [{ criterionIndex: 1, label: 'ERP', verdict: 'good', rationale: 'Skills list includes ERP, indicating direct experience with ERP systems.' }] },
  { contact: dolly, reasons: [{ criterionIndex: 1, label: 'ERP', verdict: 'good', rationale: 'Skills list includes ERP, indicating relevant experience.' }] },
  { contact: pradeep, reasons: [{ criterionIndex: 1, label: 'ERP', verdict: 'good', rationale: 'Multiple roles as SAP/ERP consultant and implementation specialist demonstrate deep ERP experience.' }] },
  ...fillers.map(c => ({ contact: c, reasons: [{ criterionIndex: 1, label: 'ERP', verdict: 'good' as const, rationale: `${c.profileData.full_name.split(' ')[0]}'s skills list includes ERP and related enterprise systems.` }] })),
];

export const SAMPLE_SEARCH: SearchEntity = {
  id: 'srchSampleSalesManagerERPDallas',
  title: 'Sales Manager ERP Dallas',
  prompt: 'Sales Manager in Dallas with experience in ERP',
  matchCount: 311,
  filterCount: 4,
  criteria: [{ id: 'crit-1', text: 'The candidate has experience in ERP.' }],
  filterValues: {
    yearsOfExp: 0,
    locations: [{ id: 'dallas', label: 'Dallas', kind: 'city' }],
    locationRadius: '25mi',
    jobTitles: ['Sales Manager'],
    skills: ['ERP'],
    contactInfoMatchType: 'any',
    onlyConnectionsType: 'none',
    hideProfilesFilters: {
      manually_hidden: { enabled: true, by: 'org-project', when: 'none' },
    },
  },
};

export const SUGGESTIONS = [
  'Software Engineers in SF working at Series B companies, skilled in Python and Node.js',
  'Marketing Manager in Europe, German-speaking, working at a large enterprise',
  'Senior Scientist in Australia, 8+ years experience',
  'Consultant in London with 2+ years experience at top consulting firms',
  'Sales Manager in Dallas with experience in ERP',
];

export const USER = { firstName: 'Toses', workspace: "Toses's Workspace" };
